<template>
  <div class="logo-upload-section" :class="{ 'has-image': hasImage }">
    <AdImagePicker
      :imageUrl="displayImageUrl"
      :uploading="isUploading"
      :hideDelete="!hasImage"
      :boxSize="hasImage"
      :width="hasImage ? undefined : '100%'"
      :height="hasImage ? undefined : 120"
      @click="triggerFileInput"
      @remove="handleRemove"
      @fileDrop="handleFileDrop" />
    <input type="file" ref="fileInput" accept=".jpg,.jpeg,.png,.gif,.webp" class="d-none" @change="onFileSelected" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import AdImagePicker from '@/components/form/AdImagePicker.vue';

const props = defineProps<{
  existingLogoUrl?: string;
}>();

const emit = defineEmits<{
  fileSelected: [file: File];
  remove: [];
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const pendingFile = ref<File | null>(null);
const pendingUrl = ref<string | null>(null);

const hasImage = computed(() => {
  return !!props.existingLogoUrl || !!pendingUrl.value;
});

const displayImageUrl = computed(() => {
  return pendingUrl.value || props.existingLogoUrl || null;
});

const triggerFileInput = () => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  const file = target.files[0];
  pendingFile.value = file;
  pendingUrl.value = URL.createObjectURL(file);

  emit('fileSelected', file);

  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const handleFileDrop = (file: File) => {
  if (isUploading.value) return;
  pendingFile.value = file;
  pendingUrl.value = URL.createObjectURL(file);
  emit('fileSelected', file);
};

const handleRemove = () => {
  if (pendingUrl.value) {
    URL.revokeObjectURL(pendingUrl.value);
  }
  pendingFile.value = null;
  pendingUrl.value = null;
  emit('remove');
};

watch(
  () => props.existingLogoUrl,
  () => {
    if (pendingUrl.value) {
      URL.revokeObjectURL(pendingUrl.value);
    }
    pendingFile.value = null;
    pendingUrl.value = null;
  }
);

defineExpose({
  pendingFile,
  isUploading,
  reset: () => {
    if (pendingUrl.value) {
      URL.revokeObjectURL(pendingUrl.value);
    }
    pendingFile.value = null;
    pendingUrl.value = null;
  },
});
</script>

<style scoped lang="scss">
.logo-upload-section {
  margin-top: 16px;

  &.has-image {
    display: flex;
    justify-content: center;
  }
}
</style>
